export interface BlogAuthor {
  name: string;
  role: string;
}

export type BlogContentBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; level: 2 | 3; text: string }
  | { type: "image"; src: string; caption?: string }
  | { type: "quote"; text: string }
  | { type: "list"; items: string[] };

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  tags: string[];
  author: BlogAuthor;
  date: string;
  readTime: string;
  image: string;
  content: BlogContentBlock[];
}

const authorBruno: BlogAuthor = {
  name: "Bruno Miranda",
  role: "Especialista em Logística, Nacional GO",
};

export const blogPosts: BlogPost[] = [
  {
    id: 1,
    slug: "transporte-de-veiculos-entenda-os-diferentes-tipos-de-servicos",
    title: "Transporte de Veículos: Entenda os Diferentes Tipos de Serviços",
    excerpt:
      "Descubra os diferentes tipos de serviços de transporte de veículos e escolha a melhor opção para a sua necessidade.",
    category: "Tipos de Serviço",
    tags: ["Transporte", "Tipos de Serviço", "Caminhão Cegonha", "Frete"],
    author: authorBruno,
    date: "26 Fev 2026",
    readTime: "6 min de leitura",
    image: "/blogs/cegonha1.webp",
    content: [
      {
        type: "paragraph",
        text: "Transportar um veículo de um estado para outro — ou mesmo dentro do mesmo estado em longas distâncias — pode parecer uma tarefa simples à primeira vista. Mas quando você analisa os riscos de dirigir centenas de quilômetros, o desgaste mecânico, o consumo de combustível e o tempo perdido, fica claro que contratar uma transportadora especializada é a escolha mais inteligente e econômica.",
      },
      {
        type: "paragraph",
        text: "O mercado de transporte de veículos oferece diferentes modalidades, cada uma pensada para um perfil de cliente e de veículo. Conhecer as opções disponíveis é o primeiro passo para fazer uma escolha consciente e garantir que o seu carro, moto ou veículo especial chegue ao destino com segurança.",
      },
      {
        type: "heading",
        level: 2,
        text: "Quais são os principais tipos de serviço de transporte de veículos?",
      },
      {
        type: "heading",
        level: 3,
        text: "Caminhão Cegonha",
      },
      {
        type: "paragraph",
        text: "O caminhão cegonha é o tipo de transporte mais popular e econômico do mercado. Ele é capaz de transportar entre 8 e 11 veículos simultaneamente, o que reduz o custo por unidade e torna o serviço acessível para a maioria dos clientes. Por ser uma opção aberta, é ideal para veículos comuns que não necessitam de proteção climática especial.",
      },
      {
        type: "heading",
        level: 3,
        text: "Caminhão Plataforma",
      },
      {
        type: "paragraph",
        text: "Indicado para veículos que não estão em condições de rodar — seja por problemas mecânicos, sinistros ou por serem veículos de coleção que nunca foram acionados. O caminhão plataforma conta com sistemas hidráulicos que permitem o carregamento sem que o veículo precise ser conduzido, garantindo segurança total durante o embarque e desembarque.",
      },
      {
        type: "heading",
        level: 3,
        text: "Envio Expresso",
      },
      {
        type: "paragraph",
        text: "Quando a urgência é o fator determinante, o envio expresso é a solução. Projetado para entregar o veículo no menor tempo possível, essa modalidade prioriza a rota do cliente, reduzindo escalas intermediárias e garantindo rapidez sem abrir mão da segurança.",
      },
      {
        type: "quote",
        text: "O envio expresso foi projetado especialmente para entregar o seu veículo no menor tempo possível, com rotas priorizadas e menos paradas intermediárias.",
      },
      {
        type: "heading",
        level: 3,
        text: "Porta a Porta",
      },
      {
        type: "paragraph",
        text: "O serviço porta a porta oferece coleta e entrega diretamente no endereço indicado pelo cliente. É a opção mais conveniente para quem não tem disponibilidade de levar ou retirar o veículo em um terminal. A comodidade é o grande diferencial dessa modalidade, que elimina deslocamentos extras e simplifica toda a logística para o cliente.",
      },
      {
        type: "heading",
        level: 3,
        text: "Caminhão Fechado",
      },
      {
        type: "paragraph",
        text: "Para veículos de alto valor, como carros de luxo, esportivos ou colecionáveis, o caminhão fechado é a escolha mais segura. A carroceria fechada protege o veículo de intempéries, poeira, chuva e outros fatores externos, garantindo que ele chegue ao destino exatamente como saiu da origem.",
      },
      {
        type: "heading",
        level: 2,
        text: "Como escolher o tipo de serviço ideal?",
      },
      {
        type: "paragraph",
        text: "A escolha do tipo de serviço depende de três fatores principais:",
      },
      {
        type: "list",
        items: [
          "Distância do transporte: rotas mais longas podem se beneficiar do envio expresso para reduzir o tempo de espera.",
          "Condição do veículo: veículos que não funcionam exigem plataforma; veículos premium pedem caminhão fechado.",
          "Nível de urgência: para entregas rápidas, o envio expresso é o mais indicado.",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Por que confiar na Nacional GO?",
      },
      {
        type: "paragraph",
        text: "A Nacional GO conta com uma rede robusta de parceiros logísticos espalhados por todo o Brasil, oferecendo todas as modalidades de transporte com rastreamento em tempo real, seguro completo e suporte dedicado ao cliente.",
      },
      {
        type: "list",
        items: [
          "Mais de 200 bases operacionais em todo o Brasil",
          "Mais de 10.000 transportadores credenciados",
          "Seguro completo de ponto a ponto",
          "Rastreamento por GPS em tempo real via plataforma e WhatsApp",
        ],
      },
      {
        type: "paragraph",
        text: "Independente da modalidade escolhida, a Nacional GO garante transparência, segurança e tecnologia em cada etapa do transporte do seu veículo.",
      },
    ],
  },
  {
    id: 2,
    slug: "transporte-de-veiculos-perguntas-frequentes-respondidas",
    title: "Transporte de Veículos: Perguntas Frequentes Respondidas",
    excerpt:
      "Tire suas principais dúvidas sobre o transporte interestadual de veículos com respostas claras e objetivas para as perguntas mais comuns.",
    category: "Perguntas Frequentes",
    tags: ["FAQ", "Documentação", "Seguro", "Rastreamento"],
    author: authorBruno,
    date: "25 Fev 2026",
    readTime: "5 min de leitura",
    image: "/blogs/caminhao1.webp",
    content: [
      {
        type: "paragraph",
        text: "O transporte interestadual de veículos ainda gera muitas dúvidas nos clientes. Questões sobre documentação, seguro, objetos pessoais e rastreamento são as mais comuns. Para simplificar o processo e trazer tranquilidade, reunimos as perguntas mais frequentes e as respondemos de forma clara e direta.",
      },
      {
        type: "heading",
        level: 2,
        text: "Quais documentos são necessários para o transporte?",
      },
      {
        type: "paragraph",
        text: "O principal documento exigido é o CRLV (Certificado de Registro e Licenciamento de Veículo), que pode ser apresentado em versão digital. Para veículos com compra recente, pode ser necessário o ATPV (Autorização para Transferência de Propriedade de Veículo). É importante que a documentação esteja em dia para evitar imprevistos durante o transporte.",
      },
      {
        type: "heading",
        level: 2,
        text: "Posso enviar objetos pessoais dentro do veículo?",
      },
      {
        type: "paragraph",
        text: "Sim! A Nacional GO permite o envio de até 150 kg de objetos pessoais dentro do veículo, por um valor adicional. É importante ressaltar que o seguro cobre exclusivamente o veículo — os pertences pessoais transportados no interior não estão cobertos pela apólice. Recomendamos retirar objetos de valor antes do embarque.",
      },
      {
        type: "heading",
        level: 2,
        text: "E se o meu veículo não estiver funcionando?",
      },
      {
        type: "paragraph",
        text: "Veículos com problemas mecânicos, sinistrados ou que simplesmente não ligam podem ser transportados normalmente. Nesses casos, utilizamos o caminhão plataforma, equipado com sistema hidráulico que dispensa a necessidade de condução do veículo. Pode haver uma taxa adicional dependendo da condição do veículo.",
      },
      {
        type: "heading",
        level: 2,
        text: "Quais são as formas de pagamento aceitas?",
      },
      {
        type: "paragraph",
        text: "A Nacional GO aceita pagamento via boleto bancário, Pix e cartão de crédito. O parcelamento pode estar disponível dependendo do valor do serviço. As condições de pagamento são informadas de forma transparente durante o processo de cotação.",
      },
      {
        type: "heading",
        level: 2,
        text: "Como posso acompanhar o meu veículo em tempo real?",
      },
      {
        type: "paragraph",
        text: "A Nacional GO oferece rastreamento em tempo real por GPS através da nossa plataforma online e também por WhatsApp. Você recebe atualizações sobre cada etapa do transporte, desde a coleta até a entrega, sem precisar ligar ou enviar e-mails para saber onde está o seu veículo.",
      },
      {
        type: "quote",
        text: "Com nossa tecnologia de rastreamento, você acompanha cada etapa do transporte do seu veículo em tempo real, diretamente pelo seu celular.",
      },
      {
        type: "heading",
        level: 2,
        text: "Existe seguro para o veículo durante o transporte?",
      },
      {
        type: "paragraph",
        text: "Sim. Todos os veículos transportados pela Nacional GO contam com seguro completo de ponto a ponto. Antes do embarque, realizamos uma vistoria fotográfica detalhada que documenta o estado do veículo. Caso ocorra qualquer avaria durante o transporte, o processo de acionamento do seguro é facilitado por essa documentação prévia.",
      },
      {
        type: "paragraph",
        text: "A Nacional GO opera desde 2010, já transportou mais de 100.000 veículos e conta com mais de 200 bases operacionais em todo o Brasil. Nossa estrutura e tecnologia garantem um serviço confiável, transparente e com o suporte que você merece.",
      },
    ],
  },
  {
    id: 3,
    slug: "a-importancia-da-logistica-no-transporte-de-veiculos",
    title: "A Importância da Logística no Transporte de Veículos",
    excerpt:
      "Entenda como a logística bem planejada garante segurança, eficiência e tranquilidade no transporte do seu veículo.",
    category: "Logística",
    tags: ["Logística", "Planejamento", "Tecnologia", "Segurança"],
    author: authorBruno,
    date: "24 Fev 2026",
    readTime: "5 min de leitura",
    image: "/blogs/caminhao2.webp",
    content: [
      {
        type: "paragraph",
        text: "Quando pensamos em transporte de veículos, é natural focar apenas no veículo em si — o carro que precisa chegar ao destino. Mas o que realmente determina o sucesso de uma operação de transporte é algo muito mais profundo: a logística por trás de todo o processo. Planejamento minucioso, tecnologia e processos bem definidos são os pilares que fazem a diferença entre uma entrega segura e um pesadelo logístico.",
      },
      {
        type: "heading",
        level: 2,
        text: "Por que a logística é fundamental no transporte de veículos?",
      },
      {
        type: "paragraph",
        text: "Uma operação de transporte de veículos envolve muito mais do que simplesmente colocar um carro em cima de um caminhão. É necessário analisar rotas, verificar documentação, preparar a frota, coordenar equipes em diferentes estados e garantir que cada etapa seja registrada e monitorada. Qualquer falha nessa cadeia pode resultar em atrasos, avarias ou prejuízos para o cliente.",
      },
      {
        type: "heading",
        level: 2,
        text: "Como uma empresa qualificada aplica a logística?",
      },
      {
        type: "paragraph",
        text: "As melhores empresas de transporte de veículos utilizam uma combinação de tecnologia e processos bem definidos para garantir a qualidade do serviço. Veja como isso se reflete na prática:",
      },
      {
        type: "list",
        items: [
          "Vistorias digitais com registro fotográfico antes e após o transporte",
          "Rastreamento em tempo real por GPS integrado à plataforma digital",
          "Seguro cobrindo o veículo durante toda a jornada",
          "Flexibilidade para transporte de objetos pessoais (até 150kg)",
          "Comunicação constante com o cliente via WhatsApp",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Tecnologia e segurança nas empresas de qualidade",
      },
      {
        type: "paragraph",
        text: "A tecnologia deixou de ser um diferencial e passou a ser uma necessidade básica no transporte de veículos. Empresas que investem em sistemas de rastreamento, plataformas digitais de acompanhamento e comunicação automatizada com o cliente oferecem um nível de transparência que gera confiança e reduz drasticamente as reclamações.",
      },
      {
        type: "quote",
        text: "Compreender a importância da logística no transporte de veículos é o primeiro passo para valorizar e proteger o seu patrimônio.",
      },
      {
        type: "heading",
        level: 2,
        text: "Burocracia simplificada pela inteligência logística",
      },
      {
        type: "paragraph",
        text: "Um dos grandes benefícios de contratar uma empresa com logística bem estruturada é a simplificação da burocracia. A documentação necessária — como CRLV Digital e ATPV — é verificada e orientada pela própria empresa, evitando que o cliente enfrente imprevistos por falta de papéis. Além disso, o processo de cotação, agendamento e pagamento acontece de forma digital e ágil.",
      },
      {
        type: "paragraph",
        text: "Ao escolher a Nacional GO, você conta com mais de uma década de experiência em logística de transporte de veículos, uma rede de mais de 200 bases operacionais e uma plataforma digital que coloca você no controle de cada etapa. A logística é a nossa especialidade — e a segurança do seu veículo é a nossa prioridade.",
      },
    ],
  },
  {
    id: 4,
    slug: "transporte-de-veiculos-como-evitar-problemas-comuns",
    title: "Transporte de Veículos: Como Evitar Problemas Comuns",
    excerpt:
      "Saiba como se prevenir dos problemas mais comuns no transporte de veículos com dicas práticas e objetivas.",
    category: "Dicas",
    tags: ["Dicas", "Problemas", "Prevenção", "Documentação"],
    author: authorBruno,
    date: "19 Fev 2026",
    readTime: "5 min de leitura",
    image: "/blogs/cegonha2.webp",
    content: [
      {
        type: "paragraph",
        text: "A maioria dos imprevistos no transporte de veículos pode ser evitada com informação e escolhas corretas. Não é necessário ter sorte para que tudo corra bem — o segredo está no planejamento e na parceria com profissionais sérios. Neste artigo, você vai conhecer os problemas mais comuns e como evitá-los.",
      },
      {
        type: "quote",
        text: "A maioria dos imprevistos pode ser prevenida com informação e escolhas corretas. O segredo está no planejamento e na parceria com profissionais sérios.",
      },
      {
        type: "heading",
        level: 2,
        text: "Escolha uma transportadora confiável",
      },
      {
        type: "paragraph",
        text: "O erro mais comum dos clientes é escolher a transportadora apenas pelo preço mais baixo. Empresas sem estrutura adequada, sem seguro e sem processos bem definidos podem causar atrasos, avarias e até perda do veículo. Pesquise a reputação da empresa, verifique se ela possui seguro, rastreamento e uma rede de atendimento sólida.",
      },
      {
        type: "heading",
        level: 2,
        text: "Providencie a documentação correta",
      },
      {
        type: "paragraph",
        text: "A documentação incompleta ou desatualizada é uma das principais causas de problemas no transporte. Certifique-se de ter em mãos:",
      },
      {
        type: "list",
        items: [
          "CRLV Digital (Certificado de Registro e Licenciamento do Veículo) válido",
          "ATPV (Autorização para Transferência de Propriedade de Veículo) para compras recentes",
          "Documentos pessoais do proprietário",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Prepare o interior do veículo",
      },
      {
        type: "paragraph",
        text: "Antes de entregar o veículo para o transporte, remova objetos soltos do interior — eles podem se mover durante o trajeto e causar danos. Se você optar por enviar objetos pessoais (permitido até 150 kg mediante taxa adicional), organize tudo de forma segura e faça uma lista do que está sendo enviado.",
      },
      {
        type: "heading",
        level: 2,
        text: "Realize uma vistoria fotográfica detalhada",
      },
      {
        type: "paragraph",
        text: "Antes do embarque, fotografe o veículo por todos os ângulos — frente, traseira, laterais, interior e teto. Essa documentação é fundamental para comprovar o estado do veículo antes do transporte e facilita muito o processo de acionamento do seguro em caso de qualquer avaria.",
      },
      {
        type: "heading",
        level: 2,
        text: "Por que a Nacional GO se destaca na prevenção de problemas?",
      },
      {
        type: "list",
        items: [
          "Rastreamento em tempo real por GPS, com atualizações via plataforma digital e WhatsApp",
          "Seguro completo de ponto a ponto incluso em todos os serviços",
          "Processo ágil e transparente para acionamento de sinistros",
          "Vistoria fotográfica realizada por nossa equipe antes do embarque",
          "Mais de 200 bases operacionais para cobertura nacional",
        ],
      },
      {
        type: "paragraph",
        text: "Com a Nacional GO, você tem a tranquilidade de saber que cada detalhe foi pensado para evitar problemas e garantir que o seu veículo chegue ao destino com segurança. Nossa experiência de mais de uma década no mercado é a maior prova de que fazemos isso com excelência.",
      },
    ],
  },
  {
    id: 5,
    slug: "guia-definitivo-para-transporte-de-veiculos",
    title: "Guia Definitivo para Transporte de Veículos: Dicas e Cuidados",
    excerpt:
      "Um guia completo com tudo o que você precisa saber para contratar um serviço de transporte de veículos com segurança e tranquilidade.",
    category: "Guias",
    tags: ["Guia", "Documentação", "Passo a Passo", "Dicas"],
    author: authorBruno,
    date: "12 Fev 2026",
    readTime: "7 min de leitura",
    image: "/blogs/cegonha3.webp",
    content: [
      {
        type: "paragraph",
        text: "Transportar um veículo entre estados é uma necessidade cada vez mais comum — seja por uma mudança, uma venda, uma compra ou simplesmente para evitar o desgaste de uma longa viagem ao volante. Mas para muitos, o processo ainda parece complicado. Este guia foi criado para simplificar tudo e te dar segurança em cada etapa.",
      },
      {
        type: "heading",
        level: 2,
        text: "Por que contratar um serviço especializado de transporte?",
      },
      {
        type: "paragraph",
        text: "Dirigir por centenas ou milhares de quilômetros expõe o motorista a riscos de acidentes, fadiga e imprevistos mecânicos. Além do risco pessoal, há o desgaste do veículo, o custo com combustível, pedágios e hospedagem. Ao contratar uma transportadora especializada, você elimina todos esses riscos e entrega a responsabilidade logística para quem realmente entende do assunto.",
      },
      {
        type: "heading",
        level: 2,
        text: "Passo a passo para contratar o transporte do seu veículo",
      },
      {
        type: "heading",
        level: 3,
        text: "1. Planejamento e cotação",
      },
      {
        type: "paragraph",
        text: "O primeiro passo é fazer uma cotação. Informe a origem, o destino, o tipo de veículo e a condição mecânica. Compare as opções disponíveis — modalidade de transporte, prazo de entrega e condições de seguro. Na Nacional GO, a cotação é feita de forma rápida e transparente, diretamente pela nossa plataforma.",
      },
      {
        type: "heading",
        level: 3,
        text: "2. Documentação necessária",
      },
      {
        type: "paragraph",
        text: "Antes do dia do transporte, separe toda a documentação necessária:",
      },
      {
        type: "list",
        items: [
          "CRLV Digital (pode ser apresentado pelo celular)",
          "ATPV para veículos com transferência recente",
          "Documento de identidade ou CNH do proprietário",
        ],
      },
      {
        type: "heading",
        level: 3,
        text: "3. Vistoria técnica",
      },
      {
        type: "paragraph",
        text: "No dia do embarque, nossa equipe realiza uma vistoria técnica completa com registro fotográfico. Esse procedimento documenta o estado do veículo antes do transporte e protege o cliente em caso de qualquer divergência na entrega.",
      },
      {
        type: "heading",
        level: 2,
        text: "Dicas para o dia do transporte",
      },
      {
        type: "list",
        items: [
          "Deixe o tanque de combustível com aproximadamente ¼ da capacidade",
          "Retire objetos de valor e pertences pessoais do interior",
          "Fotografe o veículo antes da entrega para a transportadora",
          "Verifique se o veículo está em condições de ser colocado no caminhão (se aplicável)",
          "Anote o número de protocolo e os dados do motorista responsável",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Precauções essenciais",
      },
      {
        type: "list",
        items: [
          "Verifique se a empresa possui seguro completo incluso",
          "Exija um relatório de vistoria assinado antes do embarque",
          "Confirme a data e o prazo estimado de entrega por escrito",
          "Utilize o rastreamento em tempo real para acompanhar o veículo",
        ],
      },
      {
        type: "quote",
        text: "O transporte profissional, quando bem planejado, é a opção mais segura, econômica e tranquila para quem precisa mover seu veículo.",
      },
      {
        type: "paragraph",
        text: "Com a Nacional GO, você conta com mais de uma década de expertise em transporte de veículos, uma rede de mais de 200 bases operacionais e uma plataforma digital que simplifica cada etapa do processo. Faça sua cotação agora e experimente a diferença de um transporte feito com tecnologia e cuidado.",
      },
    ],
  },
  {
    id: 6,
    slug: "transporte-de-veiculos-o-que-considerar-ao-fazer-uma-cotacao",
    title: "Transporte de Veículos: O Que Considerar ao Fazer uma Cotação",
    excerpt:
      "Descubra quais fatores influenciam o valor do frete e como fazer uma cotação inteligente para o transporte do seu veículo.",
    category: "Cotação",
    tags: ["Cotação", "Preço", "Fatores", "Economizar"],
    author: authorBruno,
    date: "18 Fev 2026",
    readTime: "5 min de leitura",
    image: "/blogs/cegonha4.webp",
    content: [
      {
        type: "paragraph",
        text: "Ao pensar em transportar um veículo, a primeira dúvida que surge é: quanto vai custar? O valor do frete varia de acordo com diversos fatores, e entendê-los é essencial para fazer uma cotação inteligente e evitar surpresas. Além disso, uma boa cotação vai muito além do preço — ela precisa contemplar segurança, prazo e qualidade do serviço.",
      },
      {
        type: "heading",
        level: 2,
        text: "Fatores que influenciam o custo do transporte",
      },
      {
        type: "heading",
        level: 3,
        text: "Distância",
      },
      {
        type: "paragraph",
        text: "A distância entre a origem e o destino é o fator mais óbvio que influencia o valor do frete. Quanto maior a distância, maior o custo. No entanto, em rotas de longa distância, o valor por quilômetro tende a ser menor do que em trajetos curtos, pois o custo fixo da operação é diluído.",
      },
      {
        type: "heading",
        level: 3,
        text: "Tipo de veículo",
      },
      {
        type: "paragraph",
        text: "Carros compactos e populares têm fretes mais baratos do que SUVs, pickups ou veículos de grande porte. O tamanho e o peso do veículo impactam diretamente a capacidade do caminhão e, consequentemente, o valor cobrado.",
      },
      {
        type: "heading",
        level: 3,
        text: "Condição mecânica",
      },
      {
        type: "paragraph",
        text: "Veículos que não estão em funcionamento exigem o uso de caminhão plataforma com sistema hidráulico, o que pode gerar um custo adicional. Se o seu veículo apresenta problemas mecânicos, informe ao fazer a cotação para receber um valor preciso.",
      },
      {
        type: "heading",
        level: 3,
        text: "Urgência da entrega",
      },
      {
        type: "paragraph",
        text: "Serviços expressos, com prazo de entrega menor, naturalmente têm um custo superior ao transporte padrão. Se o prazo não é um fator crítico, optar pelo transporte convencional pode ser uma forma de economizar.",
      },
      {
        type: "heading",
        level: 3,
        text: "Modificações no veículo",
      },
      {
        type: "paragraph",
        text: "Veículos rebaixados, alargados ou com modificações na carroceria podem ter dificuldades de embarque no caminhão cegonha padrão. Nesses casos, modalidades específicas podem ser necessárias, influenciando o custo final.",
      },
      {
        type: "heading",
        level: 2,
        text: "O que analisar além do preço?",
      },
      {
        type: "paragraph",
        text: "Uma cotação inteligente vai além do menor valor. Avalie os seguintes pontos antes de fechar o contrato:",
      },
      {
        type: "list",
        items: [
          "A empresa possui seguro completo incluso no valor?",
          "Existe rastreamento em tempo real do veículo?",
          "A vistoria fotográfica é realizada antes do embarque?",
          "O prazo de entrega é claro e contratual?",
          "A empresa tem histórico comprovado e avaliações positivas?",
        ],
      },
      {
        type: "quote",
        text: "Avaliar uma cotação vai muito além do preço. Segurança, transparência e tecnologia são os verdadeiros indicadores de uma boa contratação.",
      },
      {
        type: "heading",
        level: 2,
        text: "Documentação e vistoria",
      },
      {
        type: "paragraph",
        text: "Ao fechar a cotação, certifique-se de ter o CRLV Digital do veículo em mãos. Para transferências recentes, o ATPV também será necessário. A Nacional GO orienta o cliente sobre toda a documentação necessária durante o processo de cotação, evitando imprevistos de última hora.",
      },
      {
        type: "paragraph",
        text: "Com mais de 200 bases operacionais e mais de 100.000 veículos transportados desde 2010, a Nacional GO oferece cotações transparentes, competitivas e com toda a segurança que você precisa. Faça sua cotação agora e comprove.",
      },
    ],
  },
  {
    id: 7,
    slug: "dicas-para-transporte-seguro-de-veiculos-em-longas-distancias",
    title: "Dicas para Transporte Seguro de Veículos em Longas Distâncias",
    excerpt:
      "Confira as melhores dicas para garantir um transporte seguro e tranquilo do seu veículo em longas distâncias.",
    category: "Dicas",
    tags: ["Dicas", "Longas Distâncias", "Segurança", "Documentação"],
    author: authorBruno,
    date: "4 Fev 2026",
    readTime: "6 min de leitura",
    image: "/blogs/cegonha5.webp",
    content: [
      {
        type: "paragraph",
        text: "O transporte de veículos em longas distâncias apresenta desafios únicos. Rotas extensas, diferentes condições climáticas e a necessidade de coordenação logística em múltiplos estados exigem planejamento cuidadoso e a escolha de uma empresa especializada. Com as dicas certas, esse processo pode ser muito mais simples e tranquilo do que parece.",
      },
      {
        type: "heading",
        level: 2,
        text: "Por que o transporte especializado é a melhor escolha para longas distâncias?",
      },
      {
        type: "paragraph",
        text: "Dirigir por mais de mil quilômetros expõe o motorista a riscos consideráveis: fadiga, acidentes, quebras mecânicas e custos imprevistos com combustível, pedágios e hospedagem. Ao optar pelo transporte especializado, você elimina esses riscos e transfere a responsabilidade logística para quem entende do assunto.",
      },
      {
        type: "quote",
        text: "Ao entregar seu veículo para uma transportadora consolidada, você transfere a responsabilidade logística para quem entende do assunto — e ganha tranquilidade em troca.",
      },
      {
        type: "heading",
        level: 2,
        text: "Dicas de preparação antes do transporte",
      },
      {
        type: "heading",
        level: 3,
        text: "Organize a documentação",
      },
      {
        type: "paragraph",
        text: "Antes de contratar o serviço, certifique-se de ter toda a documentação necessária em dia:",
      },
      {
        type: "list",
        items: [
          "CRLV Digital (Certificado de Registro e Licenciamento do Veículo)",
          "ATPV (para veículos com transferência recente de propriedade)",
          "Documento de identidade ou CNH do proprietário",
        ],
      },
      {
        type: "heading",
        level: 3,
        text: "Atenção na vistoria",
      },
      {
        type: "paragraph",
        text: "Realize uma vistoria minuciosa no veículo antes do embarque. Fotografe todos os ângulos — danos pré-existentes, riscos, amassados e o estado dos pneus e vidros. Essa documentação é sua proteção em caso de qualquer divergência na entrega.",
      },
      {
        type: "heading",
        level: 3,
        text: "Objetos pessoais",
      },
      {
        type: "paragraph",
        text: "Se precisar enviar objetos pessoais junto com o veículo, lembre-se que o limite é de 150 kg (mediante taxa adicional). O seguro cobre apenas o veículo, não os pertences transportados no interior. Organize tudo de forma segura e faça uma lista completa.",
      },
      {
        type: "heading",
        level: 2,
        text: "Como escolher a empresa certa para longas distâncias?",
      },
      {
        type: "paragraph",
        text: "Para rotas longas, é fundamental escolher uma empresa com ampla cobertura nacional. Verifique se a empresa possui bases operacionais na origem e no destino, seguro completo incluso, rastreamento em tempo real e um canal de comunicação dedicado ao cliente. A Nacional GO possui mais de 200 bases operacionais em todo o Brasil e tecnologia de rastreamento em tempo real.",
      },
      {
        type: "heading",
        level: 2,
        text: "Modalidades disponíveis para longas distâncias",
      },
      {
        type: "list",
        items: [
          "Caminhão Cegonha: a opção mais econômica, ideal para veículos comuns",
          "Caminhão Fechado: proteção total para veículos de luxo ou de alto valor",
          "Porta a Porta: coleta e entrega no endereço indicado, máxima conveniência",
          "Envio Expresso: para quem precisa do veículo no destino o mais rápido possível",
        ],
      },
      {
        type: "paragraph",
        text: "O transporte de veículos em longas distâncias se torna simples quando você conta com a documentação certa e uma transportadora de confiança. A Nacional GO combina tecnologia, experiência e uma rede logística nacional para garantir que o seu veículo chegue ao destino com segurança, independente da distância.",
      },
    ],
  },
  {
    id: 8,
    slug: "transporte-de-veiculos-comparacao-entre-modalidades",
    title: "Transporte de veículos: comparação entre modalidades",
    excerpt:
      "Conheça as diferenças, custos e vantagens do transporte de veículos e escolha a alternativa mais segura e econômica.",
    category: "Tipos de Serviço",
    tags: ["Transporte de veículos", "Cegonha", "Caminhão Fechado", "Modalidades"],
    author: authorBruno,
    date: "2 Fev 2026",
    readTime: "5 min de leitura",
    image: "/blogs/cegonha6.webp",
    content: [
      {
        type: "paragraph",
        text: "O transporte de veículos no Brasil não é padronizado: existem diversas modalidades especializadas, cada uma pensada para um perfil diferente de cliente e de veículo. A escolha certa depende do valor do veículo, da urgência da entrega e da condição mecânica do carro.",
      },
      {
        type: "heading",
        level: 2,
        text: "Caminhão Cegonha",
      },
      {
        type: "paragraph",
        text: "O caminhão cegonha é o método mais comum e econômico do Brasil. Capaz de transportar até 11 carros de passeio ou 8 utilitários simultaneamente, o custo é distribuído entre múltiplos clientes, tornando o serviço acessível. É ideal para veículos comuns que não necessitam de proteção climática especial.",
      },
      {
        type: "heading",
        level: 2,
        text: "Caminhão Fechado (Sider)",
      },
      {
        type: "paragraph",
        text: "Para veículos de luxo, coleções clássicas e esportivos de alto valor, o caminhão fechado oferece proteção superior. A carroceria fechada protege contra intempéries, poeira e detritos da estrada, com segurança e discrição aprimoradas.",
      },
      {
        type: "heading",
        level: 2,
        text: "Plataforma ou Serviço de Guincho",
      },
      {
        type: "paragraph",
        text: "Indicado para transportes exclusivos ou veículos que não estão em condições de rodar — com falhas mecânicas ou elétricas. O sistema hidráulico permite o carregamento sem que o veículo precise ser conduzido.",
      },
      {
        type: "heading",
        level: 2,
        text: "Modalidades de entrega",
      },
      {
        type: "paragraph",
        text: "O serviço porta a porta oferece conveniência total, com coleta e entrega no endereço indicado. Já o serviço terminal a terminal reduz custos para quem pode levar e retirar o veículo em um dos mais de 200 pontos de apoio da Nacional GO em todo o Brasil.",
      },
      {
        type: "heading",
        level: 2,
        text: "Diferenciais do serviço",
      },
      {
        type: "list",
        items: [
          "Transporte de itens pessoais: até 150 kg com taxa adicional",
          "Rastreamento em tempo real via plataforma ou WhatsApp",
          "Cobertura de seguro completa independente da modalidade escolhida",
          "Mais de 200 pontos de apoio em todo o território nacional",
        ],
      },
      {
        type: "paragraph",
        text: "Ao comparar as modalidades, leve em conta não apenas o preço, mas também a proteção oferecida e as necessidades específicas do seu veículo. A Nacional GO orienta cada cliente na escolha da melhor opção.",
      },
    ],
  },
  {
    id: 9,
    slug: "como-escolher-a-melhor-empresa-de-transporte-de-veiculos",
    title: "Como escolher a melhor empresa de transporte de veículos",
    excerpt:
      "Vai transportar seu veículo? Confira um checklist essencial para filtrar opções e contratar uma empresa de transporte segura e confiável.",
    category: "Dicas",
    tags: ["Empresa de Transporte", "Checklist", "Segurança", "Confiabilidade"],
    author: authorBruno,
    date: "2 Fev 2026",
    readTime: "5 min de leitura",
    image: "/blogs/cegonha7.webp",
    content: [
      {
        type: "paragraph",
        text: "Confiar o transporte do seu veículo a terceiros é uma decisão importante. A escolha de uma transportadora não deve se basear apenas no custo — critérios como reputação, seguro, tecnologia e cobertura nacional são fundamentais para garantir que seu carro chegue ao destino em perfeitas condições.",
      },
      {
        type: "heading",
        level: 2,
        text: "Reputação e avaliações dos clientes",
      },
      {
        type: "paragraph",
        text: "Pesquise depoimentos de clientes que indicam pontualidade, chegada do veículo em condição adequada e qualidade do atendimento. Uma empresa consolidada terá um histórico verificável de serviços bem executados.",
      },
      {
        type: "heading",
        level: 2,
        text: "Seguro e cobertura completos",
      },
      {
        type: "paragraph",
        text: "A proteção de ponta a ponta contra roubo, furto, acidentes e danos menores é inegociável para quem busca verdadeira segurança. Verifique se o seguro está incluso no contrato e quais são as coberturas exatas.",
      },
      {
        type: "heading",
        level: 2,
        text: "Rastreamento em tempo real",
      },
      {
        type: "paragraph",
        text: "A capacidade de monitorar continuamente a localização do veículo — por plataforma digital ou WhatsApp — oferece transparência e tranquilidade durante todo o processo de transporte.",
      },
      {
        type: "heading",
        level: 2,
        text: "Vistoria detalhada antes do embarque",
      },
      {
        type: "paragraph",
        text: "Empresas sérias documentam o estado do veículo por meio de fotografias e relatórios detalhados antes do transporte. Essa vistoria protege o cliente em caso de qualquer divergência na entrega.",
      },
      {
        type: "heading",
        level: 2,
        text: "Diferenciais que fazem a diferença",
      },
      {
        type: "list",
        items: [
          "Transporte de itens pessoais: até 150 kg de roupas e pertences domésticos mediante taxa",
          "Variedade de serviços: cegonha (custo-benefício), caminhão fechado (veículos premium), porta a porta (máxima conveniência)",
          "Cobertura nacional: mais de 200 bases e atendimento em mais de 3.500 municípios",
          "Rastreamento real, seguro completo e inspeção documentada",
        ],
      },
      {
        type: "quote",
        text: "Segurança não é negociável: exija seguro completo, rastreamento em tempo real e vistoria documentada antes de assinar qualquer contrato.",
      },
      {
        type: "paragraph",
        text: "A Nacional GO reúne todos esses critérios em um único serviço, com mais de uma década de experiência no mercado e tecnologia integrada para garantir a tranquilidade do cliente do início ao fim.",
      },
    ],
  },
  {
    id: 10,
    slug: "contratar-servico-de-transporte-de-veiculos",
    title: "Passo a passo para contratar serviço de transporte de veículos",
    excerpt:
      "Veja o passo a passo para contratar serviço de transporte de veículos com cotação, vistoria e rastreamento para total segurança.",
    category: "Guias",
    tags: ["Contratar", "Passo a Passo", "Cotação", "Documentação"],
    author: authorBruno,
    date: "27 Jan 2026",
    readTime: "5 min de leitura",
    image: "/blogs/contratar.webp",
    content: [
      {
        type: "paragraph",
        text: "Contratar um serviço de transporte de veículos pode parecer complexo, mas com o processo correto é simples e seguro. Conheça cada etapa para garantir que seu veículo chegue ao destino sem imprevistos.",
      },
      {
        type: "heading",
        level: 2,
        text: "1. Solicitação de cotação",
      },
      {
        type: "paragraph",
        text: "O primeiro passo é fornecer os dados do veículo: modelo, cidade de origem, destino e quaisquer modificações especiais que possam afetar a logística. Com essas informações, a transportadora calcula o valor e os prazos disponíveis.",
      },
      {
        type: "heading",
        level: 2,
        text: "2. Escolha da modalidade de serviço",
      },
      {
        type: "paragraph",
        text: "Duas opções principais estão disponíveis:",
      },
      {
        type: "list",
        items: [
          "Caminhão cegonha: transportador aberto, econômico para veículos comuns",
          "Caminhão fechado: para veículos de luxo ou colecionáveis que exigem proteção contra clima e poeira",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "3. Verificação de documentação",
      },
      {
        type: "paragraph",
        text: "O CRLV Digital (Certificado de Registro e Licenciamento de Veículo) deve estar em dia e sem pendências. Para veículos adquiridos recentemente, o documento ATP-V (Autorização para Transferência de Propriedade) também é necessário.",
      },
      {
        type: "heading",
        level: 2,
        text: "4. Vistoria antes do embarque",
      },
      {
        type: "paragraph",
        text: "Fotos reais documentam o estado exato do veículo antes do transporte, garantindo total transparência. Esse registro é fundamental para proteger o cliente em caso de qualquer divergência na entrega.",
      },
      {
        type: "heading",
        level: 2,
        text: "Recursos adicionais do serviço",
      },
      {
        type: "list",
        items: [
          "Rastreamento em tempo real via plataforma ou WhatsApp",
          "Permissão para transportar até 150 kg de pertences pessoais (com taxa adicional)",
          "Cobertura de seguro completa durante todo o transporte",
          "Mais de 200 bases de apoio em todo o Brasil",
        ],
      },
      {
        type: "paragraph",
        text: "Seguindo esses passos, você garante uma contratação segura e sem surpresas. A Nacional GO orienta o cliente em cada etapa, do primeiro contato até a entrega do veículo no destino.",
      },
    ],
  },
  {
    id: 11,
    slug: "rastreamento-de-veiculos",
    title: "Rastreamento de veículos: uma ferramenta essencial no transporte",
    excerpt:
      "Saiba por que o rastreamento de veículos é indispensável para a segurança, redução de custos e eficiência na gestão moderna do transporte.",
    category: "Tecnologia",
    tags: ["Rastreamento", "GPS", "Segurança", "Tecnologia"],
    author: authorBruno,
    date: "16 Jan 2026",
    readTime: "5 min de leitura",
    image: "/blogs/rastreamento.webp",
    content: [
      {
        type: "paragraph",
        text: "O rastreamento de veículos evoluiu muito além de um simples GPS. Hoje, sistemas modernos oferecem dados em tempo real sobre localização, velocidade, paradas e histórico de rotas — informações que vão muito além do posicionamento e se tornaram indispensáveis para qualquer operação logística séria.",
      },
      {
        type: "heading",
        level: 2,
        text: "Por que o rastreamento é essencial?",
      },
      {
        type: "list",
        items: [
          "Prevenção de roubos e desvios de rota não autorizados",
          "Garantia de conformidade regulatória com as normas da ANTT",
          "Resposta rápida a problemas operacionais durante o transporte",
          "Transparência total entre empresa transportadora e cliente",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Como funciona o rastreamento na Nacional GO",
      },
      {
        type: "paragraph",
        text: "A Nacional GO integra o rastreamento como parte padrão do serviço. Os clientes podem monitorar o veículo em transporte por uma plataforma online ou receber atualizações via WhatsApp sobre as etapas de entrega — sem precisar ligar ou enviar e-mails.",
      },
      {
        type: "quote",
        text: "Com rastreamento em tempo real, você sabe exatamente onde está o seu veículo em cada momento do transporte.",
      },
      {
        type: "heading",
        level: 2,
        text: "Tecnologia como diferencial de qualidade",
      },
      {
        type: "paragraph",
        text: "O rastreamento não é apenas um recurso de segurança — é um diferencial de qualidade que combina tecnologia com estratégia logística. Empresas que investem em sistemas de monitoramento oferecem ao cliente o controle e a confiabilidade que ele precisa para confiar no serviço.",
      },
      {
        type: "paragraph",
        text: "A Nacional GO opera com mais de 200 pontos de apoio em todo o Brasil e mantém uma rede de transportadores credenciados, todos com rastreamento ativo durante o transporte do seu veículo.",
      },
    ],
  },
  {
    id: 12,
    slug: "seguranca-no-transporte-de-veiculos",
    title: "Como é feita a segurança da carga no transporte de veículos",
    excerpt:
      "Saiba como garantir a segurança no transporte de veículos. Conheça as técnicas de amarração, rastreamento e os seguros obrigatórios.",
    category: "Segurança",
    tags: ["Segurança", "Amarração", "Seguro", "ANTT"],
    author: authorBruno,
    date: "14 Jan 2026",
    readTime: "5 min de leitura",
    image: "/blogs/seguranca.webp",
    content: [
      {
        type: "paragraph",
        text: "A segurança no transporte de veículos vai muito além de simplesmente colocar o carro no caminhão. Um conjunto de mecanismos técnicos, legais e operacionais garante que o veículo chegue ao destino exatamente como saiu da origem.",
      },
      {
        type: "heading",
        level: 2,
        text: "Etapas da proteção durante o transporte",
      },
      {
        type: "heading",
        level: 3,
        text: "1. Inspeção técnica",
      },
      {
        type: "paragraph",
        text: "Antes do embarque, os veículos passam por uma inspeção detalhada com checklists digitais e registro fotográfico completo. Essa documentação protege tanto o cliente quanto a transportadora em caso de qualquer questionamento posterior.",
      },
      {
        type: "heading",
        level: 3,
        text: "2. Fixação profissional",
      },
      {
        type: "paragraph",
        text: "Os veículos são fixados nas plataformas com cintas, calços e travas de rodas para evitar movimentos laterais ou verticais durante o trânsito. Essa técnica é aplicada por profissionais treinados e segue normas técnicas específicas.",
      },
      {
        type: "heading",
        level: 3,
        text: "3. Monitoramento em tempo real",
      },
      {
        type: "paragraph",
        text: "Todos os caminhões possuem rastreamento via satélite com monitoramento 24 horas de velocidade, paradas e localização. Os clientes têm acesso a essas informações pela plataforma digital.",
      },
      {
        type: "heading",
        level: 2,
        text: "Cobertura legal e de seguros",
      },
      {
        type: "list",
        items: [
          "Operações em conformidade com as normas da ANTT e CONTRAN",
          "Apólices RCTR-C e RCA que protegem contra colisões, roubos e incêndios",
          "Documentação fiscal CTeD e MDF-e que garante a legalidade do transporte",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Modalidades de transporte e suas proteções",
      },
      {
        type: "list",
        items: [
          "Caminhões cegonha: transporte multivetorial padrão com fixação profissional",
          "Plataformas individuais: para veículos que não estão em operação",
          "Transporte fechado: proteção premium para veículos de luxo e colecionáveis",
        ],
      },
      {
        type: "paragraph",
        text: "A Nacional GO aplica todos esses protocolos em cada operação, garantindo que o seu veículo seja tratado com o máximo cuidado do embarque à entrega.",
      },
    ],
  },
  {
    id: 13,
    slug: "transporte-de-veiculos-em-fortaleza",
    title: "Como realizar o transporte de veículos em Fortaleza com segurança total",
    excerpt:
      "Precisa realizar o transporte de veículos em Fortaleza? Confira dicas essenciais para garantir a segurança total do seu carro ou frota.",
    category: "Regional",
    tags: ["Fortaleza", "Nordeste", "Transporte de veículos", "Segurança"],
    author: authorBruno,
    date: "12 Jan 2026",
    readTime: "5 min de leitura",
    image: "/blogs/cegonha8.webp",
    content: [
      {
        type: "paragraph",
        text: "O transporte de veículos para ou a partir de Fortaleza exige atenção especial à segurança e ao uso de serviços profissionais. A capital do Ceará é um importante hub logístico do Nordeste, e contar com uma empresa especializada faz toda a diferença na qualidade da operação.",
      },
      {
        type: "heading",
        level: 2,
        text: "Por que contratar serviços profissionais?",
      },
      {
        type: "paragraph",
        text: "Seguir as normas da ANTT (Resolução 6.033/2023) minimiza a exposição a riscos durante o trânsito. Empresas credenciadas operam dentro das exigências regulatórias, garantindo que o transporte seja legal, seguro e rastreável.",
      },
      {
        type: "heading",
        level: 2,
        text: "Modalidades disponíveis para Fortaleza",
      },
      {
        type: "list",
        items: [
          "Caminhão cegonha: transporte múltiplo e econômico para veículos comuns",
          "Serviço de guincho/plataforma: para veículos que não estão em funcionamento",
          "Entrega porta a porta: coleta e entrega no endereço indicado para máxima comodidade",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Documentação necessária",
      },
      {
        type: "list",
        items: [
          "CRLV Digital (Certificado de Registro e Licenciamento de Veículo)",
          "ATP-V (Autorização para Transferência de Propriedade) quando aplicável",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Checklist de preparação do veículo",
      },
      {
        type: "list",
        items: [
          "Limpe o veículo para facilitar a vistoria fotográfica",
          "Mantenha o nível de combustível baixo (cerca de ¼ do tanque)",
          "Remova objetos pessoais de valor do interior",
          "Verifique e anote a quilometragem atual",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Seguros e cobertura",
      },
      {
        type: "paragraph",
        text: "As proteções obrigatórias RCTR-C e RCF-DC estão cobertas, além de opções adicionais contra roubo e danos. O prazo padrão de entrega para Fortaleza é de 3 a 7 dias úteis, com opções expressas disponíveis.",
      },
      {
        type: "quote",
        text: "Com rastreamento GPS em tempo real e uma rede de mais de 200 bases nacionais, a Nacional GO garante o transporte seguro do seu veículo até Fortaleza.",
      },
      {
        type: "paragraph",
        text: "A Nacional GO possui base operacional credenciada na região Nordeste e transportadores habilitados para a rota de Fortaleza, garantindo segurança e eficiência em cada etapa do processo.",
      },
    ],
  },
];

export const getPostBySlug = (slug: string): BlogPost | undefined =>
  blogPosts.find((p) => p.slug === slug);

export const getRelatedPosts = (post: BlogPost, count = 3): BlogPost[] => {
  const same = blogPosts.filter(
    (p) => p.id !== post.id && p.category === post.category,
  );
  const others = blogPosts.filter(
    (p) => p.id !== post.id && p.category !== post.category,
  );
  return [...same, ...others].slice(0, count);
};

export const getAllCategories = (): string[] => {
  const cats = blogPosts.map((p) => p.category);
  return Array.from(new Set(cats));
};
